// ── Step 1: Batch Analysis Module ────────────────────────────────
// Owns: step-input panel DOM, batch job lifecycle, navigation advance to step 2.

import {
  getState,
  setState,
  setStateBulk,
  resetDraftSessionState,
  subscribe,
} from './state.js';
import {
  parseAsinsFromText,
  getErrorMessage,
} from './utils.js';
import { ASIN_REGEX } from './constants.js';
import { syncNavigationBounds } from './navigation-bounds.js';
import { batchFacade } from './interaction/facades/batch.facade.instance.js';
import { closeModalDialog, openModalDialog } from './interaction/ui/modal-dialog.js';
import { isCurrentBatchJobId } from './step1/batch-job-identity.js';
import { createBatchPollController } from './step1/batch-poll-controller.js';
import { resetStep3AutoSaveScope } from './step3/autosave.js';
import { lockActiveProjectBinder } from './modules/shared/project-binder-access.js';
const {
  createBatch,
  getBatchJob,
  checkSif,
} = batchFacade;

// ── DOM References ───────────────────────────────────────────────

const $ = (sel) => document.querySelector(sel);
const $$ = (sel) => document.querySelectorAll(sel);
const REFERENCE_SELLING_POINTS_SOURCE = 'reference_only';
let sellingPointsSource = 'manual';

function splitSellingPoints(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

export function validateProductFactReadiness() {
  return { ok: true, message: '' };
}

function getStep1Readiness() {
  const category = $('#product-category')?.value.trim() || '';
  const marketplaceSelect = $('#product-marketplace');
  const marketplace = marketplaceSelect?.selectedOptions?.[0]?.textContent?.trim() || marketplaceSelect?.value || 'US';
  const sellingPoints = splitSellingPoints($('#product-selling-points')?.value || '');
  const asins = parseAsinsFromText($('#product-asins')?.value || '');
  const asinReferencePlan = buildAsinReferencePlan(asins);

  let summary = '还差竞品 ASIN';
  if (asins.length === 0) {
    summary = '还差竞品 ASIN';
  } else if (asins.length > 5) {
    summary = '竞品 ASIN 超过 5 个';
  } else if (asins.length > 1 && !asinReferencePlan.isComplete) {
    summary = '请确认款式与主题参考 ASIN';
  } else {
    summary = '可以开始分析';
  }

  return {
    category: category || '将从竞品自动识别',
    marketplace,
    sellingPoints,
    asins,
    summary,
  };
}

function referencePlanSelectOptions(asins, selected, placeholder) {
  const options = [];
  if (asins.length > 1) {
    options.push(Object.assign(document.createElement('option'), {
      value: '',
      textContent: placeholder,
    }));
  }
  for (const asin of asins) {
    const option = Object.assign(document.createElement('option'), {
      value: asin,
      textContent: asin,
    });
    option.selected = asin === selected;
    options.push(option);
  }
  return options;
}

function selectedOptionValues(select) {
  return Array.from(select?.selectedOptions || [])
    .map((option) => option.value)
    .filter(Boolean);
}

function buildReferenceRoles({ styleReferenceAsin, themeReferenceAsin, supplementalReferenceAsins = [] } = {}) {
  if (!styleReferenceAsin || !themeReferenceAsin) return [];
  const authoritative = styleReferenceAsin === themeReferenceAsin
    ? [{ asin: styleReferenceAsin, role: 'combined' }]
    : [
        { asin: styleReferenceAsin, role: 'style' },
        { asin: themeReferenceAsin, role: 'theme' },
      ];
  const reserved = new Set(authoritative.map((entry) => entry.asin));
  return [
    ...authoritative,
    ...[...new Set(supplementalReferenceAsins)]
      .filter((asin) => !reserved.has(asin))
      .map((asin) => ({ asin, role: 'supplemental' })),
  ];
}

export function buildAsinReferencePlan(asins = []) {
  const validAsins = [...new Set((asins || []).filter((asin) => ASIN_REGEX.test(asin)))];
  const styleSelect = $('#product-style-reference-asin');
  const themeSelect = $('#product-theme-reference-asin');
  const supplementalSelect = $('#product-supplemental-reference-asins');
  const defaultAsin = validAsins[0] || '';
  const styleReferenceAsin = validAsins.includes(styleSelect?.value) ? styleSelect.value : defaultAsin;
  const themeReferenceAsin = validAsins.includes(themeSelect?.value) ? themeSelect.value : defaultAsin;
  const supplementalReferenceAsins = selectedOptionValues(supplementalSelect)
    .filter((asin) => validAsins.includes(asin))
    .filter((asin) => asin !== styleReferenceAsin && asin !== themeReferenceAsin);
  const needsConfirmation = validAsins.length > 1;
  const isComplete = !needsConfirmation || Boolean(
    styleSelect?.value
    && themeSelect?.value
    && validAsins.includes(styleSelect.value)
    && validAsins.includes(themeSelect.value)
  );
  const referenceRoles = isComplete
    ? buildReferenceRoles({ styleReferenceAsin, themeReferenceAsin, supplementalReferenceAsins })
    : [];
  return {
    schemaVersion: 'asin-reference-plan.v1',
    styleReferenceAsin,
    themeReferenceAsin,
    usesSeparateReferences: Boolean(
      styleReferenceAsin
      && themeReferenceAsin
      && styleReferenceAsin !== themeReferenceAsin
    ),
    usesCombinedReference: referenceRoles.some((entry) => entry.role === 'combined'),
    referenceRoles,
    supplementalReferenceAsins: referenceRoles
      .filter((entry) => entry.role === 'supplemental')
      .map((entry) => entry.asin),
    selectionSource: validAsins.length === 1
      ? 'single_asin_default'
      : isComplete
        ? 'user_selected'
        : 'pending_user_selection',
    isComplete,
  };
}

export function renderAsinReferencePlan() {
  const panel = $('#asin-reference-plan-panel');
  const styleSelect = $('#product-style-reference-asin');
  const themeSelect = $('#product-theme-reference-asin');
  const supplementalSelect = $('#product-supplemental-reference-asins');
  const supplementalWrap = $('#product-supplemental-reference-wrap');
  const hint = $('#asin-reference-plan-hint');
  if (!panel || !styleSelect || !themeSelect || !supplementalSelect) return;

  const asins = [...new Set(parseAsinsFromText($('#product-asins')?.value || '')
    .filter((asin) => ASIN_REGEX.test(asin)))];
  const priorStyle = styleSelect.value;
  const priorTheme = themeSelect.value;
  const priorSupplemental = selectedOptionValues(supplementalSelect);
  panel.hidden = asins.length < 2;
  if (supplementalWrap) supplementalWrap.hidden = asins.length < 3;

  if (!asins.length) {
    styleSelect.replaceChildren();
    themeSelect.replaceChildren();
    supplementalSelect.replaceChildren();
    if (hint) hint.textContent = '';
    return;
  }

  const styleValue = asins.includes(priorStyle) ? priorStyle : (asins.length === 1 ? asins[0] : '');
  const themeValue = asins.includes(priorTheme) ? priorTheme : (asins.length === 1 ? asins[0] : '');
  styleSelect.replaceChildren(...referencePlanSelectOptions(asins, styleValue, '请选择款式基准'));
  themeSelect.replaceChildren(...referencePlanSelectOptions(asins, themeValue, '请选择主题基准'));
  styleSelect.value = styleValue;
  themeSelect.value = themeValue;
  const reserved = new Set([styleValue, themeValue].filter(Boolean));
  supplementalSelect.replaceChildren(...asins
    .filter((asin) => !reserved.has(asin))
    .map((asin) => Object.assign(document.createElement('option'), {
      value: asin,
      textContent: asin,
      selected: priorSupplemental.includes(asin),
    })));
  if (hint) {
    hint.textContent = asins.length === 1
      ? '单个 ASIN 将作为综合参考，同时提供款式事实与主题方向。'
      : '请确认款式与主题参考；改款通常选择不同 ASIN。未指定的 ASIN 不参与产品事实，可按需选择为普通补充。';
  }
}

function renderStep1Readiness() {
  const card = $('#step1-readiness-card');
  if (!card) return;

  const state = getStep1Readiness();
  const categoryEl = $('#step1-ready-category');
  const marketplaceEl = $('#step1-ready-marketplace');
  const pointsEl = $('#step1-ready-points');
  const asinsEl = $('#step1-ready-asins');
  const summaryEl = $('#step1-ready-summary');

  if (categoryEl) categoryEl.textContent = state.category;
  if (marketplaceEl) marketplaceEl.textContent = state.marketplace;
  if (pointsEl) pointsEl.textContent = `${state.sellingPoints.length} 条`;
  if (asinsEl) asinsEl.textContent = `${state.asins.length} 个`;

  // PR7: 项目可选；未绑定也可开始分析（no-project 模式）
  const hasProject = !!getState('newProductProjectId');
  const startBtn = $('#start-batch-button');
  if (startBtn) {
    const formReady = state.summary === '可以开始分析';
    startBtn.disabled = !formReady;
    startBtn.title = !formReady
      ? '请先完成表单填写'
      : (hasProject ? '' : '未绑定项目时将以无项目模式开始分析');
  }

  if (summaryEl) {
    summaryEl.textContent = state.summary;
    summaryEl.classList.toggle('is-ready', state.summary === '可以开始分析');
  }
}

// ── 市场数据调用费用确认 Modal ─────────────────────────────────

function renderMcpCostDetail(container, calls, cost) {
  container.replaceChildren(
    document.createTextNode('本次分析预计调用 '),
    Object.assign(document.createElement('strong'), { textContent: String(calls) }),
    document.createTextNode(' 次市场数据接口'),
    document.createElement('br'),
    document.createTextNode('费用约 '),
    Object.assign(document.createElement('strong'), { textContent: `¥${cost}` }),
    document.createTextNode('（1 次 = ¥0.1）')
  );
}

function showMcpCostModal(mcpCalls, mcpCost) {
  return new Promise((resolve) => {
    const modal = $('#mcp-cost-modal');
    const detail = $('#mcp-cost-detail');
    const sifDetail = $('#mcp-cost-sif-detail');
    const cancelBtn = $('#mcp-cost-cancel');
    const confirmBtn = $('#mcp-cost-confirm');
    const sifBtn = $('#mcp-cost-sif');
    if (!modal || !detail || !cancelBtn || !confirmBtn) { resolve('market'); return; }

    const cleanup = (result) => {
      closeModalDialog(modal);
      cancelBtn.onclick = null;
      confirmBtn.onclick = null;
      if (sifBtn) sifBtn.onclick = null;
      resolve(result);
    };
    renderMcpCostDetail(detail, mcpCalls, mcpCost);
    if (sifDetail) renderMcpCostDetail(sifDetail, 1, '0.1');
    openModalDialog(modal, { onRequestClose: () => cleanup(null) });
    cancelBtn.onclick = () => cleanup(null);
    confirmBtn.onclick = () => cleanup('market');
    if (sifBtn) sifBtn.onclick = () => cleanup('sif');
  });
}

// ── UI Helpers ──────────────────────────────────────────────────

function setLoading(on) {
  const btn = $('#start-batch-button');
  if (!btn) return;
  btn.disabled = on;
  btn.textContent = on ? '分析中...' : '开始批量分析';
}

function showStatus(msg, type = 'info') {
  const el = $('#batch-status');
  if (!el) return;
  el.textContent = msg;
  el.dataset.tone = type;
}

function isSifReady(payload) {
  return payload?.status === 'ready' || payload?.login?.authenticated;
}

function isSifUnhealthy(payload) {
  return payload?.status === 'unhealthy' || payload?.healthy === false;
}

async function ensureSifReadyBeforeQuery(marketplace) {
  showStatus('正在检查 SIF 模组状态...');
  const status = await checkSif();
  if (isSifReady(status)) return true;
  if (isSifUnhealthy(status)) {
    const err = new Error('模组异常，请联系管理员');
    err.code = 'SIF_MODULE_UNHEALTHY';
    throw err;
  }

  showStatus('SIF 登录态失效，请通知管理员在系统管理 > SIF 登录中扫码。', 'error');
  const err = new Error('SIF 登录态失效，请通知管理员在系统管理 > SIF 登录中扫码');
  err.code = 'SIF_LOGIN_REQUIRED';
  throw err;
}

function renderPhaseBar(phase, status) {
  const phases = $$('#batch-progress .phase-item');
  const PHASE_ORDER = ['scraping', 'building_keywords', 'completed'];
  const currentIdx = PHASE_ORDER.indexOf(phase);
  const phaseIdx = PHASE_ORDER.indexOf(status);

  phases.forEach((el) => {
    const p = el.dataset.phase;
    const pi = PHASE_ORDER.indexOf(p);

    if (status === 'completed') {
      el.dataset.state = 'completed';
    } else if (status === 'failed') {
      if (pi === phaseIdx) el.dataset.state = 'failed';
      else if (pi < phaseIdx) el.dataset.state = 'completed';
    } else if (p === phase) {
      el.dataset.state = 'active';
    } else if (pi < currentIdx) {
      el.dataset.state = 'completed';
    } else {
      el.dataset.state = '';
    }
  });
}

function renderEvents(events) {
  const list = $('#batch-events-list');
  if (!list || !events) return;
  list.replaceChildren(...events
    .slice(-30)
    .map((e) => {
      const time = e.ts
        ? new Date(e.ts).toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
        : '';
      const item = document.createElement('li');
      item.textContent = `[${time}] ${e.message || ''}`;
      return item;
    }));
}

// ── Batch Lifecycle ─────────────────────────────────────────────

const batchPollController = createBatchPollController({
  getBatchJob,
  isCurrentJobId: (jobId) => isCurrentBatchJobId(getState('batchJobId'), jobId),
  onResponse: (data) => {
    renderPhaseBar(data.phase, data.status);
    renderEvents(data.events);
  },
  onPending: (data) => {
    const progress = data.progress || {};
    const phaseLabel = data.status === 'queued' ? '排队中' : '进行中';
    showStatus(`${phaseLabel}: ${progress.completed || 0}/${progress.total || 0} 个 ASIN`);
  },
  onCompleted: onBatchComplete,
  onFailed: (data) => {
    showStatus(`失败: ${data.error || '未知错误'}`, 'error');
    setLoading(false);
  },
  onUnknown: (data) => {
    showStatus(`未知状态: ${data.status}`, 'error');
    setLoading(false);
  },
  onRetry: (error, { attempt, maxRetries }) => {
    showStatus(`轮询暂时失败，正在重试（${attempt}/${maxRetries}）: ${getErrorMessage(error)}`, 'info');
  },
  onError: (error) => {
    showStatus(`轮询失败: ${getErrorMessage(error)}`, 'error');
    setLoading(false);
  },
});

let batchPollLifecycleBound = false;

function clearPollTimer() {
  batchPollController.clear();
}

async function startBatchAnalysis() {
  const category = $('#product-category')?.value.trim() || '';
  const marketplace = String($('#product-marketplace')?.value || getState('marketplace') || 'US').trim().toUpperCase() || 'US';
  const sellingPoints = $('#product-selling-points')?.value.trim() || '';
  const sellingPointItems = splitSellingPoints(sellingPoints);
  const resolvedSellingPointsSource = sellingPointItems.length ? sellingPointsSource : 'unknown';
  const asinText = $('#product-asins')?.value || '';

  const asins = parseAsinsFromText(asinText);
  if (asins.length === 0) {
    showStatus('请至少输入一个 ASIN', 'error');
    return;
  }

  const invalid = asins.filter((a) => !ASIN_REGEX.test(a));
  if (invalid.length > 0) {
    showStatus(`无效 ASIN: ${invalid.join(', ')}`, 'error');
    return;
  }

  const asinReferencePlan = buildAsinReferencePlan(asins);
  if (!asinReferencePlan.isComplete) {
    showStatus('请先确认款式基准 ASIN 与主题基准 ASIN', 'error');
    return;
  }

  // 选择数据获取方式（市场工具 / SIF 精准查询）
  // A distinct theme ASIN needs one additional traffic-keyword query. Include
  // it in the confirmation rather than under-reporting market-tool usage.
  const mcpCalls = asins.length + 7 + (asinReferencePlan.usesSeparateReferences ? 1 : 0);
  const mcpCost = (mcpCalls * 0.1).toFixed(1);
  const dataMode = await showMcpCostModal(mcpCalls, mcpCost);
  if (!dataMode) {
    return; // 用户取消
  }
  if (
    dataMode === 'sif'
    && asins.length > 1
    && (
      asinReferencePlan.styleReferenceAsin !== asins[0]
      || asinReferencePlan.themeReferenceAsin !== asins[0]
    )
  ) {
    showStatus('SIF 精准查询当前仅支持首个 ASIN，无法同时使用独立的款式/主题参考。请改用市场工具模式。', 'error');
    return;
  }
  clearPollTimer();
  // 创建新商品任务前先撤销旧 job 的自动保存回写权，避免延迟
  // HTTP 响应把 savedListingId 或保存提示带到新商品。
  resetStep3AutoSaveScope();
  resetDraftSessionState({ dataMode, marketplace });
  syncNavigationBounds();

  setLoading(true);
  showStatus('正在创建批量分析任务...');
  $('#batch-progress').hidden = false;
  $('#batch-events-section').hidden = false;

  try {
    if (dataMode === 'sif') {
      await ensureSifReadyBeforeQuery(marketplace);
      showStatus('SIF 模组已就绪，正在创建查询任务...');
    }
    const data = await createBatch({
      productInfo: {
        category,
        sellingPoints,
        sellingPointItems,
        sellingPointsSource: resolvedSellingPointsSource,
        marketplace,
        targetMarket: marketplace,
        asinReferencePlan,
      },
      asins,
      marketplace,
      dataMode,
      newProductProjectId: getState('newProductProjectId') || null,
    });
    if (data.error) throw new Error(data.error);

    setState('batchJobId', data.id);
    setState('productInfo', {
      category,
      sellingPoints,
      sellingPointItems,
      sellingPointsSource: resolvedSellingPointsSource,
      marketplace,
      targetMarket: marketplace,
      asinReferencePlan,
    });
    // Job already carries project context — freeze binder so users cannot rebind mid-run.
    if (getState('newProductProjectId')) lockActiveProjectBinder('locked');
    pollBatchJob(data.id);
  } catch (err) {
    const message = getErrorMessage(err);
    showStatus(message === '模组异常，请联系管理员' ? message : `失败: ${message}`, 'error');
    setLoading(false);
  }
}

async function pollBatchJob(jobId) {
  return batchPollController.poll(jobId);
}

function onBatchComplete(data) {
  clearPollTimer();
  setLoading(false);
  showStatus('分析完成!', 'ok');

  const { keywordLibrary, snapshots, businessContext } = data;
  const completed = (snapshots || []).filter((s) => s && s.status === 'completed' && s.asin);

  setStateBulk({
    batchJobId: data.id,
    batchJob: data,
    keywordLibrary,
    businessContext: businessContext || null,
    keywordUsePolicies: businessContext?.keywordUsePolicies || [],
    competitorRoles: businessContext?.competitorRoles || [],
    batchSnapshots: completed,
    competitorAsinsIncluded: new Set(completed.map((s) => s.asin)),
    selectedCompetitorIndex: 0,
    snapshotState: completed[0] || null,
    keywordConflictResult: data.keywordConflicts?.length ? { conflicts: data.keywordConflicts } : null,
    lastDetectedConflicts: Array.isArray(data.keywordConflicts) ? data.keywordConflicts : [],
    stepReady: { ...(getState('stepReady') || {}), 2: true },
  });
  syncNavigationBounds();

  // Unlock step 2 and navigate
  const highest = getState('highestStep');
  setState('highestStep', Math.max(highest, 2));
  setState('currentStep', 2);
}

// ── Module Init ─────────────────────────────────────────────────

async function resumePersistedBatchJob() {
  const jobId = getState('batchJobId');
  if (!jobId || getState('keywordLibrary')?.keywords?.length) return;
  try {
    const data = await getBatchJob(jobId);
    if (!isCurrentBatchJobId(getState('batchJobId'), jobId)) return;
    const jobProjectId = data?.newProductProjectId
      ?? data?.new_product_project_id
      ?? null;
    if (jobProjectId != null && Number(jobProjectId) > 0) {
      setState('newProductProjectId', Number(jobProjectId));
      lockActiveProjectBinder('locked');
    }
    if (data.status === 'completed' && data.keywordLibrary?.keywords?.length) {
      onBatchComplete(data);
      return;
    }
    if (data.status === 'running' || data.status === 'queued') {
      setState('batchJobId', data.id);
      setState('batchJob', data);
      pollBatchJob(data.id);
      return;
    }
    setStateBulk({ batchJobId: '', batchJob: null });
  } catch {
    // 任务可能仍在运行；交给带退避的轮询器恢复，而不是在一次网络抖动后丢弃会话。
    pollBatchJob(jobId);
  }
}

export function initStep1() {
  // 从 URL 参数预填表单（来自选品页面跳转）
  prefillFromUrlParams();
  renderAsinReferencePlan();

  // Bind "开始批量分析" button
  const btn = $('#start-batch-button');
  btn?.addEventListener('click', startBatchAnalysis);

  ['product-category', 'product-marketplace', 'product-selling-points'].forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.addEventListener('input', () => {
      if (id === 'product-selling-points') sellingPointsSource = 'manual';
      renderStep1Readiness();
    });
    el.addEventListener('change', renderStep1Readiness);
  });

  const asinsEl = $('#product-asins');
  const refreshReferencePlan = () => {
    renderAsinReferencePlan();
    renderStep1Readiness();
  };
  asinsEl?.addEventListener('input', refreshReferencePlan);
  asinsEl?.addEventListener('change', refreshReferencePlan);
  ['product-style-reference-asin', 'product-theme-reference-asin', 'product-supplemental-reference-asins'].forEach((id) => {
    document.getElementById(id)?.addEventListener('change', refreshReferencePlan);
  });

  const marketplaceEl = $('#product-marketplace');
  if (marketplaceEl?.value) {
    setState('marketplace', String(marketplaceEl.value).trim().toUpperCase() || 'US');
  }
  marketplaceEl?.addEventListener('change', () => {
    setState('marketplace', String(marketplaceEl.value || 'US').trim().toUpperCase() || 'US');
  });

  // Subscribe to batchJobId being cleared (reset UI)
  subscribe('state:batchJobIdChanged', ({ prev, curr }) => {
    if (prev && String(prev) !== String(curr || '')) clearPollTimer();
    if (!curr) {
      setLoading(false);
      showStatus('');
      setStateBulk({
        batchJob: null,
        keywordConflictResult: null,
        lastDetectedConflicts: [],
      });
    }
  });

  // 项目绑定状态变化时刷新按钮门禁
  subscribe('state:newProductProjectIdChanged', () => renderStep1Readiness());

  renderStep1Readiness();
  resumePersistedBatchJob();

  if (!batchPollLifecycleBound) {
    batchPollLifecycleBound = true;
    window.addEventListener('pagehide', clearPollTimer);
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted || getState('keywordLibrary')?.keywords?.length) return;
      const jobId = getState('batchJobId');
      if (jobId) void pollBatchJob(jobId);
    });
  }
}

/**
 * 从 URL 参数预填表单（来自选品页面跳转）
 * 支持参数：asins（空格或逗号分隔）、site（US/DE/FR/...）、category（品类文本）、
 * selling_points（换行分隔的卖点列表）、selling_points_source（manual/reference_only）
 */
function prefillFromUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const asinsText = params.get('asins') || params.get('asin') || '';
  const site = params.get('site') || params.get('marketplace') || '';
  const category = params.get('category') || '';
  const sellingPointsText = params.get('selling_points') || params.get('sellingPoints') || '';
  const sellingPointsSourceParam = String(
    params.get('selling_points_source') || params.get('sellingPointsSource') || '',
  ).trim().toLowerCase();

  let changed = false;

  // 站点
  if (site) {
    const siteCode = site.trim().toUpperCase();
    const marketplaceEl = $('#product-marketplace');
    if (marketplaceEl) {
      const option = Array.from(marketplaceEl.options).find((o) => o.value.toUpperCase() === siteCode);
      if (option) {
        marketplaceEl.value = option.value;
        setState('marketplace', option.value);
        changed = true;
      }
    }
  }

  // 竞品 ASIN（支持空格/逗号分隔多个）
  if (asinsText) {
    const asinsEl = $('#product-asins');
    if (asinsEl) {
      // 统一用空格分隔，parseAsinsFromText 默认按空白拆分
      const normalized = asinsText.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      if (normalized) {
        asinsEl.value = normalized;
        changed = true;
      }
    }
  }

  // 品类
  if (category) {
    const categoryEl = $('#product-category');
    if (categoryEl) {
      categoryEl.value = category.trim();
      changed = true;
    }
  }

  // 核心卖点（来自选品 AI 提取，换行分隔多条）
  if (sellingPointsText) {
    const spEl = $('#product-selling-points');
    if (spEl) {
      // 将换行分隔的卖点整理为可读的多行文本
      const points = sellingPointsText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (points.length) {
        spEl.value = points.join('\n');
        sellingPointsSource = sellingPointsSourceParam === REFERENCE_SELLING_POINTS_SOURCE
          ? REFERENCE_SELLING_POINTS_SOURCE
          : 'manual';
        changed = true;
      }
    }
  }

  if (changed) {
    // 触发 input 事件，让 readiness 检测更新
    ['product-category', 'product-marketplace', 'product-asins', 'product-selling-points'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}
